// Manager correction of one time entry (spec 26 api_time_edit_entry,
// PATCH /v1/time/entries/:id). The server gates it on time_attendance.edit plus
// the row-scope grant and refuses a locked entry or an entry whose timesheet is
// approved/locked (time.timesheet_locked); the caller only hides the row action.

import { useEffect, useId, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import {
  Banner,
  Button,
  FormField,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Textarea,
} from "@novemai/components";

import { EnvelopeError, errorMessage } from "@/api/errors";
import { isoToLocalDateTime, localDateTimeToIso } from "@/lib/local-date-time";

import { formatTimeInstant } from "./format-instant";
import { correctTimeEntry, type TimeEntryCorrectionInput, type TimeEntryRow } from "./time-api";

/** Route schema limit for edit_reason (PatchEntryBodySchema). */
const REASON_MAX = 2000;

interface CorrectionIntent {
  readonly body: TimeEntryCorrectionInput;
  readonly key: string;
}

type FieldError = "invalid" | "order" | null;

/**
 * Entry statuses the server may accept an edit for. `locked` is always refused;
 * `adjusted` stays offered because the server allows it once the parent
 * timesheet is reopened, and only the server sees that parent state.
 */
export function isCorrectableEntry(entry: TimeEntryRow): boolean {
  return !["locked", "voided", "rejected"].includes(entry.status);
}

export function EntryCorrectionDialog({
  entry,
  timeZone,
  onClose,
  onPendingChange,
}: {
  readonly entry: TimeEntryRow;
  readonly timeZone: string;
  readonly onClose: () => void;
  readonly onPendingChange: (pending: boolean) => void;
}): React.ReactElement {
  const { t, i18n } = useTranslation();
  const formatInstant = (iso: string): string => formatTimeInstant(i18n.language, timeZone, iso);
  const client = useQueryClient();
  const titleId = useId();
  const initialStart = isoToLocalDateTime(entry.start_at, timeZone);
  const initialEnd = isoToLocalDateTime(entry.end_at, timeZone);
  // An open punch has no end yet; writing one here would close it without the
  // clock-out transition, so only a closed entry offers the end field.
  const hasEnd = entry.end_at !== null;
  const [start, setStart] = useState(initialStart);
  const [end, setEnd] = useState(initialEnd);
  const [reason, setReason] = useState("");
  const [attempted, setAttempted] = useState(false);
  const [pending, setPending] = useState<CorrectionIntent | null>(null);

  useEffect(() => {
    onPendingChange(pending !== null);
  }, [onPendingChange, pending]);

  const mutation = useMutation({
    mutationFn: (intent: CorrectionIntent) => correctTimeEntry(entry.entry_id, intent.body, intent.key),
    onSuccess: () => {
      setPending(null);
      void client.invalidateQueries({ queryKey: ["time"] });
      onClose();
    },
    onError: (error: unknown) => {
      // A 4xx is a definitive refusal; network/5xx may have committed, so the
      // same body and key stay retained for a replay.
      if (error instanceof EnvelopeError && error.httpStatus >= 400 && error.httpStatus < 500) {
        setPending(null);
        void client.invalidateQueries({ queryKey: ["time"] });
      }
    },
  });

  const startIso = localDateTimeToIso(start, timeZone);
  const endIso = hasEnd ? localDateTimeToIso(end, timeZone) : null;
  const startError: FieldError = startIso === null ? "invalid" : null;
  let endError: FieldError = null;
  if (hasEnd) {
    if (endIso === null) endError = "invalid";
    else if (startIso !== null && Date.parse(endIso) <= Date.parse(startIso)) endError = "order";
  }
  const trimmedReason = reason.trim();
  const reasonMissing = trimmedReason === "";
  const startChanged = start !== initialStart;
  const endChanged = hasEnd && end !== initialEnd;
  const unchanged = !startChanged && !endChanged;
  const valid = startError === null && endError === null && !reasonMissing && !unchanged;

  const frozen = pending !== null || mutation.isPending;
  const close = (): void => {
    if (!frozen) onClose();
  };

  function submit(): void {
    if (mutation.isPending) return;
    if (pending !== null) {
      mutation.mutate(pending);
      return;
    }
    setAttempted(true);
    if (!valid || startIso === null) return;
    const body: TimeEntryCorrectionInput = {
      ...(startChanged && { start_at: startIso }),
      ...(endChanged && endIso !== null && { end_at: endIso }),
      edit_reason: trimmedReason,
    };
    const intent: CorrectionIntent = { body, key: crypto.randomUUID() };
    setPending(intent);
    mutation.mutate(intent);
  }

  const fieldErrorText = (error: FieldError): string | undefined =>
    error === null ? undefined : t(`time.pdks.correction.error_${error}`);

  return (
    <Modal
      open
      aria-labelledby={titleId}
      onClose={close}
      closeOnEscape={!frozen}
      closeOnScrimClick={!frozen}
    >
      <ModalHeader
        id={titleId}
        title={
          entry.employee_name === null
            ? t("time.pdks.correction.title")
            : t("time.pdks.correction.title_named", { name: entry.employee_name })
        }
      />
      <ModalBody>
        <div className="space-y-4">
          <p className="font-sans text-[14px] leading-relaxed text-[var(--fg-secondary)]">
            {t("time.pdks.correction.description", { timeZone })}
          </p>
          <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 rounded-lg border border-[var(--border-default)] px-3 py-2 text-sm">
            <dt className="text-[var(--fg-secondary)]">{t("time.pdks.correction.recorded_start")}</dt>
            <dd className="text-[var(--fg-primary)]">{formatInstant(entry.start_at)}</dd>
            <dt className="text-[var(--fg-secondary)]">{t("time.pdks.correction.recorded_end")}</dt>
            <dd className="text-[var(--fg-primary)]">
              {entry.end_at === null ? t("time.cell.dash") : formatInstant(entry.end_at)}
            </dd>
          </dl>
          <FormField
            label={t("time.pdks.correction.start_label")}
            required
            error={attempted ? fieldErrorText(startError) : undefined}
          >
            <Input
              type="datetime-local"
              value={start}
              disabled={frozen}
              onChange={(event): void => setStart(event.target.value)}
            />
          </FormField>
          {hasEnd ? (
            <FormField
              label={t("time.pdks.correction.end_label")}
              required
              error={attempted ? fieldErrorText(endError) : undefined}
            >
              <Input
                type="datetime-local"
                value={end}
                disabled={frozen}
                onChange={(event): void => setEnd(event.target.value)}
              />
            </FormField>
          ) : (
            <p className="text-sm text-[var(--fg-secondary)]">{t("time.pdks.correction.open_entry")}</p>
          )}
          <FormField
            label={t("time.pdks.correction.reason_label")}
            hint={t("time.pdks.correction.reason_hint")}
            required
            error={attempted && reasonMissing ? t("time.pdks.correction.error_reason") : undefined}
          >
            <Textarea
              value={reason}
              maxLength={REASON_MAX}
              disabled={frozen}
              onChange={(event): void => setReason(event.target.value)}
            />
          </FormField>
          {attempted && unchanged && pending === null ? (
            <p role="alert" className="text-sm text-[var(--state-danger)]">
              {t("time.pdks.correction.error_unchanged")}
            </p>
          ) : null}
          {mutation.isError ? (
            <Banner severity="danger" title={t("time.pdks.correction.error_title")}>
              {errorMessage(mutation.error, t)}
            </Banner>
          ) : null}
          {pending !== null && !mutation.isPending ? (
            <p role="status" className="text-sm text-[var(--fg-secondary)]">
              {t("time.pdks.correction.retry_hint")}
            </p>
          ) : null}
        </div>
      </ModalBody>
      <ModalFooter>
        <Button variant="secondary" size="sm" disabled={frozen} onClick={close}>
          {t("time.pdks.correction.cancel")}
        </Button>
        <Button variant="primary" size="sm" disabled={mutation.isPending} onClick={submit}>
          {pending !== null && !mutation.isPending
            ? t("time.pdks.correction.retry")
            : t("time.pdks.correction.save")}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
